import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";

import NavBar from "../../components/navBar/navBar";
import logUserAction from "../../apis/userLogger";
import { __API_URL__ } from "../../SERVER_URL";
import useGetNewEcrfs from "../../hooks/useGetNewEcrfs";
import useDuplicateSurveyMutation from "../../hooks/useDuplicateSurveyMutation";
import EcrfPopUpMessage from "../../components/PopUp/EcrfPopUpMessage";
import Loader from "../../components/Loader";

const SurveyListWidget = () => {
  const token = localStorage.getItem("token");
  const [popMessage, setPopMessage] = useState(false);
  const navigate = useNavigate();
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [pageItemCount, setPageItemCount] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);

  const DeleteDocument = (id) => {
    setDeleteItemId(id);
    setPopMessage(true);
  };

  const confirmDelete = (id) => {
    if (id) {
      axios
        .delete(`${__API_URL__}/api/ecrf/delete_new_ecrf/${id}/`, {
          headers: {
            Authorization: `Token ${token}`,
          },
        })
        .then(() => {
          toast.success("File Deleted Successfully");
          setItems(items?.filter((item) => item.id !== id));
        })
        .catch(() => {
          toast.error("Something Went Wrong");
        })
        .finally(() => {
          setPopMessage(false);
        });
    }
  };

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage);
  };

  const { data: newEcrfsData = [], isLoading: loader } = useGetNewEcrfs();

  useEffect(() => {
    if (newEcrfsData) {
      setItems(newEcrfsData);
    }
  }, [newEcrfsData]);

  const { mutate: duplicateSurvey } = useDuplicateSurveyMutation();

  const handleDuplicateClick = (surveyId) => {
    duplicateSurvey({ surveyId });
  };

  const filteredItems = items?.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const startIndex = (currentPage - 1) * pageItemCount;
  const endIndex = startIndex + pageItemCount;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="form_creater" />

      <Paper sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            p: 2,
            width: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                navigate("/form-creater-v2/");
                logUserAction("Click on Create eCRF Block Button");
              }}
            >
              Create eCRF Block
            </Button>
          </Stack>
        </Box>

        {loader ? (
          <Loader />
        ) : (
          <TableContainer sx={{ maxHeight: "60vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Search by Block Name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>Creator</TableCell>
                  <TableCell>Trials</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Delete</TableCell>
                  <TableCell>Edit Block</TableCell>
                  <TableCell>Copy Block</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.length > 0 ? (
                  paginatedItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.user_email}</TableCell>
                      <TableCell>{item.trials?.join(", ")}</TableCell>
                      <TableCell>{item.created_at}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => DeleteDocument(item?.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/form-creater-v2/?surveyId=${item.id}`)}
                        >
                          <EditTwoToneIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDuplicateClick(item?.id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        There is No Source Document (eCRF) Blocks.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {popMessage && (
          <EcrfPopUpMessage
            setPopMessage={setPopMessage}
            confirmDelete={confirmDelete}
            id={deleteItemId}
          />
        )}

        {filteredItems.length > pageItemCount && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={pageItemCount}
                onChange={(e) => setPageItemCount(parseInt(e.target.value))}
              >
                {[10, 25, 50, 100].map((item) => (
                  <MenuItem key={item} value={item}>
                    Show {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Pagination
              count={Math.ceil(filteredItems.length / pageItemCount)}
              page={currentPage}
              onChange={(e, page) => handlePageChange(page)}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SurveyListWidget;
